import { Component, OnInit, OnDestroy, Inject, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSliderModule } from '@angular/material/slider';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { ProductStoreService, Product } from '../../services/productstore.service';
import { CategoryService, Category } from '../../services/category.service';

@Component({
  selector: 'app-products',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatCheckboxModule,
    MatSliderModule,
    FormsModule
  ],
  templateUrl: './products.component.html',
  styleUrls: ['./products.component.css']
})
export class ProductsComponent implements OnInit, OnDestroy {
  products: Product[] = [];
  filteredProducts: Product[] = [];
  categories: Category[] = [];
  
  // Subscription management
  private productsSubscription: Subscription | null = null;
  
  // Modal
  showProductDetails = false;
  selectedProduct: Product | null = null;
  
  // Filters
  selectedCategory = 'all';
  searchQuery = '';
  sortBy = 'name';
  priceRange = [0, 1000];
  showCustomizable = false;
  
  // View mode
  viewMode: 'grid' | 'list' = 'grid';
  
  // Loading
  isLoading = true;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private productStore: ProductStoreService,
    private categoryService: CategoryService
  ) {}

  ngOnInit() {
    this.loadProducts();
    this.loadCategories();
  }

  ngOnDestroy() {
    if (this.productsSubscription) {
      this.productsSubscription.unsubscribe();
    }
  }

  loadProducts() {
    this.isLoading = true;
    if (this.productsSubscription) {
      this.productsSubscription.unsubscribe();
    }
    this.productsSubscription = this.productStore.getAllProducts().subscribe({
      next: (response) => {
        if ((response.success || response.status === 'success') && response.data) {
          const rawProducts = response.data;
          this.products = rawProducts.map((rawProduct: any) => this.transformProduct(rawProduct))
            .filter((product: Product) => this.shouldShowOnProductsPage(product));
          this.applyFilters();
          this.loadCategories();
        } else {
          this.products = [];
        }
        this.isLoading = false;
      },
      error: (error) => {
        this.products = [];
        this.isLoading = false;
      }
    });
  }

  // Transform raw API product to our Product interface
  private transformProduct(rawProduct: any): Product {
    return {
      _id: rawProduct.id?.toString(),
      id: rawProduct.id,
      name: rawProduct.name,
      category: rawProduct.category,
      categoryId: rawProduct.category_id || rawProduct.category,
      price: Number(rawProduct.price),
      originalPrice: Number(rawProduct.original_price || rawProduct.originalPrice || 0),
      image: rawProduct.image,
      images: Array.isArray(rawProduct.images) ? rawProduct.images : [],
      rating: Number(rawProduct.rating || 0),
      reviews: Number(rawProduct.reviews || 0),
      isNew: this.parseBoolean(rawProduct.is_new || rawProduct.isNew),
      isSale: this.parseBoolean(rawProduct.is_sale || rawProduct.isSale),
      description: rawProduct.description || '',
      shortDescription: rawProduct.short_description || rawProduct.shortDescription,
      isCustomizable: this.parseBoolean(rawProduct.is_customizable || rawProduct.isCustomizable),
      customizationOptions: Array.isArray(rawProduct.customization_options) 
        ? rawProduct.customization_options 
        : (Array.isArray(rawProduct.customizationOptions) ? rawProduct.customizationOptions : []),
      showOnHome: this.parseBoolean(rawProduct.show_on_home || rawProduct.showOnHome),
      showOnProducts: this.parseBoolean(rawProduct.show_on_products || rawProduct.showOnProducts),
      status: rawProduct.status || 'active'
    };
  }

  // Helper method to parse boolean values from API response
  private parseBoolean(value: any): boolean {
    if (typeof value === 'boolean') {
      return value;
    }
    if (typeof value === 'string') {
      return value.toLowerCase() === 'true';
    }
    return false;
  }

  // Check if product should show on products page
  private shouldShowOnProductsPage(product: Product): boolean {
    // If showOnProducts is undefined or null, default to true for backward compatibility
    let shouldShow = true;
    if (product.showOnProducts !== undefined && product.showOnProducts !== null) {
      shouldShow = product.showOnProducts;
    }
    return shouldShow; 
  }

  loadCategories() {
    this.categories = [
      { id: 'all', name: 'All Categories', displayName: 'All Categories' },
      { id: 'cotton-bags', name: 'Cotton Bags', displayName: 'Cotton Bags' },
      { id: 'canvas-tote-bags', name: 'Canvas Tote Bags', displayName: 'Canvas Tote Bags' },
      { id: 'jute-bags', name: 'Jute Bags', displayName: 'Jute Bags' },
      { id: 'field-sample-bag', name: 'Field Sample Bags', displayName: 'Field Sample Bags' }
    ];
    this.categories.forEach(category => {
      if (category.id === 'all') {
        category.count = this.products.length;
      } else {
        category.count = this.products.filter(p => p.category === category.id || p.categoryId === category.id).length;
      }
    });
  }

  applyFilters() {
    let filtered = [...this.products];
    if (this.selectedCategory !== 'all') {
      filtered = filtered.filter(product => 
        product.category === this.selectedCategory || product.categoryId === this.selectedCategory
      );
    }
    if (this.searchQuery) {
      const query = this.searchQuery.toLowerCase();
      filtered = filtered.filter(product =>
        product.name.toLowerCase().includes(query) ||
        product.description.toLowerCase().includes(query) ||
        (product.isCustomizable && 'customize'.includes(query)) ||
        (product.customizationOptions && product.customizationOptions.some((option: string) =>
          option.toLowerCase().includes(query)
        ))
      );
    }
    switch (this.sortBy) {
      case 'name':
        filtered.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'price-low':
        filtered.sort((a, b) => a.price - b.price);
        break;
      case 'price-high':
        filtered.sort((a, b) => b.price - a.price);
        break;
      case 'rating':
        filtered.sort((a, b) => b.rating - a.rating);
        break;
      case 'newest':
        filtered.sort((a, b) => (b.isNew ? 1 : 0) - (a.isNew ? 1 : 0));
        break;
    }
    this.filteredProducts = filtered;
  }

  // Missing method: Handle search input change
  onSearchChange(event: any) {
    this.searchQuery = event.target.value.toLowerCase();
    this.applyFilters();
  }

  // Missing method: Handle category change
  onCategoryChange(categoryId: string) {
    this.selectedCategory = categoryId;
    this.applyFilters();
    this.loadCategories(); // Update counts
  }

  onSortChange() {
    this.applyFilters();
  }

  // Missing method: Toggle view mode
  toggleView(mode: 'grid' | 'list') {
    this.viewMode = mode;
  }

  // Missing method: Reset all filters
  resetFilters() {
    this.selectedCategory = 'all';
    this.searchQuery = '';
    this.sortBy = 'name';
    this.priceRange = [0, 1000];
    this.showCustomizable = false;
    this.applyFilters();
    this.loadCategories();
  }

  viewProductDetails(product: Product): void {
    this.selectedProduct = product;
    this.showProductDetails = true;
    if (isPlatformBrowser(this.platformId)) {
      document.body.style.overflow = 'hidden';
    }
  }

  closeProductDetails(): void {
    this.showProductDetails = false;
    this.selectedProduct = null;
    if (isPlatformBrowser(this.platformId)) {
      document.body.style.overflow = 'auto';
    }
  }

  knowMoreOnWhatsApp(product: Product): void {
    let message = `Hello Aadinath Enterprises!

I'm interested in your product: *${product.name}*

Product Details:
- Price: ₹${product.price}
- Category: ${this.getCategoryDisplayName(product.category)}
- Rating: ${product.rating}/5`;

    if (product.customizationOptions && product.customizationOptions.length > 0) {
      message += `\n- Customization Options: ${product.customizationOptions.map((option: string) => `• ${option}`).join('\n')}`;
    }

    message += `\n\nCould you please provide more information about:
- Minimum order quantity
- Bulk pricing
- Customization details
- Delivery timeline

Thank you!`;

    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/919876543210?text=${encodedMessage}`;
    
    if (isPlatformBrowser(this.platformId)) {
      window.open(whatsappUrl, '_blank');
    }
  }

  getCategoryDisplayName(category: string): string {
    const categoryMap: { [key: string]: string } = {
      'field-sample-bag': 'Field Sample Bags',
      'cotton-bags': 'Cotton Bags',
      'canvas-tote-bags': 'Canvas Tote Bags',
      'jute-bags': 'Jute Bags'
    };
    return categoryMap[category] || category;
  }

  getStars(rating: number): number[] {
    return Array(5).fill(0).map((_, i) => i < Math.floor(rating) ? 1 : 0);
  }

  customizeInquiry(product: Product): void {
    this.knowMoreOnWhatsApp(product);
  }

  onImageError(event: any) {
    event.target.src = '/assets/images/placeholder.jpg';
  }
}
